// TOUR-4a — per-step fly/highlight effect for the tour session controller.

import { useEffect, type Dispatch, type MutableRefObject, type RefObject } from "react";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { AnchorEntitlements } from "@/lib/entitlements/anchorsClient";
import { ACTIVITY_DOCK_OPEN_EVENT } from "@/components/activity/ActivityFloatingDock";
import {
  getBucketForMode,
  resolveActiveBucket,
} from "@/components/shell/modes/bucketsRegistry";
import { fireflyBucketIdFromNavTarget } from "@/lib/firefly/fireflyBucketNavTargets";
import {
  FIREFLY_TOUR_DOCK_ANCHOR_IDS,
  resolveFireflyTourTravelOrigin,
} from "@/lib/firefly/fireflyTourTravelOrigin";
import {
  readFireflyTargetRect,
  scrollFireflyTargetIntoView,
} from "@/lib/firefly/fireflyTargetConvention";
import { isFireflyModeNavTarget } from "@/lib/firefly/fireflyTargetIntegrity";
import {
  shouldAutoNavigateAfterBucketPick,
  shouldDelaySkipWhenAlreadyOnMode,
  tourAdvanceEventForSatisfiedStep,
  tourStepSettleMs,
} from "@/lib/firefly/fireflyTourStepNavigation";
import { waitForFireflyTarget } from "@/lib/firefly/waitForFireflyTarget";
import { waitForPrimaryMode } from "@/lib/firefly/waitForPrimaryMode";
import type { FireflyTourRunnerEvent, FireflyTourRunnerState } from "@/lib/firefly/tourTypes";

export type UseFireflyTourStepEffectOptions = {
  currentMode: PrimaryMode;
  runner: FireflyTourRunnerState;
  dispatch: Dispatch<FireflyTourRunnerEvent>;
  setHighlightReady: (ready: boolean) => void;
  flyTo: (from: { x: number; y: number }, to: { x: number; y: number }) => Promise<void>;
  cancelTravel: () => void;
  clearTimer: () => void;
  clearClickListener: () => void;
  timerRef: RefObject<number | null>;
  clickCleanupRef: MutableRefObject<(() => void) | null>;
  lastAnchorCenterRef: MutableRefObject<{ x: number; y: number } | null>;
  currentModeRef: MutableRefObject<PrimaryMode>;
  authRef: MutableRefObject<AuthContextValue>;
  anchorsRef: MutableRefObject<AnchorEntitlements | null>;
  onNavigateToModeRef: MutableRefObject<(mode: PrimaryMode) => void>;
  onOpenDock?: () => void;
};

export function useFireflyTourStepEffect(options: UseFireflyTourStepEffectOptions): void {
  const {
    currentMode,
    runner,
    dispatch,
    setHighlightReady,
    flyTo,
    cancelTravel,
    clearTimer,
    clearClickListener,
    timerRef,
    clickCleanupRef,
    lastAnchorCenterRef,
    currentModeRef,
    authRef,
    anchorsRef,
    onNavigateToModeRef,
    onOpenDock,
  } = options;

  useEffect(() => {
    if (runner.status !== "running" || !runner.currentStep) {
      setHighlightReady(false);
      cancelTravel();
      clearTimer();
      clearClickListener();
      return;
    }

    let cancelled = false;
    const stepSnapshot = runner.currentStep;
    const { step } = stepSnapshot;
    const prevStep = runner.currentIndex > 0 ? runner.steps[runner.currentIndex - 1] : null;
    const afterBucketPick = prevStep?.id.startsWith("prelude-bucket-pick-") ?? false;

    const bindStepInteractions = (el: HTMLElement | null) => {
      clearClickListener();
      if (stepSnapshot.waitForUserAction && el) {
        const onClick = () => dispatch({ type: "user_action" });
        el.addEventListener("click", onClick, { once: true });
        clickCleanupRef.current = () => el.removeEventListener("click", onClick);
      }

      clearTimer();
      if (!stepSnapshot.waitForUserAction && stepSnapshot.advanceOn === "timer") {
        timerRef.current = window.setTimeout(() => {
          dispatch({ type: "timer_elapsed" });
        }, stepSnapshot.dwellMs);
      }
    };

    if (step.skipWhenCurrentMode === currentMode) {
      const advanceType = tourAdvanceEventForSatisfiedStep(stepSnapshot.waitForUserAction);
      if (shouldDelaySkipWhenAlreadyOnMode(prevStep?.id)) {
        const timer = window.setTimeout(() => {
          if (!cancelled) dispatch({ type: advanceType });
        }, tourStepSettleMs(prevStep?.id));
        return () => {
          cancelled = true;
          window.clearTimeout(timer);
        };
      }
      dispatch({ type: advanceType });
      return;
    }

    const targetBucketId = fireflyBucketIdFromNavTarget(step.target.anchorId);
    if (targetBucketId && stepSnapshot.waitForUserAction) {
      const activeBucket = resolveActiveBucket(
        currentMode,
        authRef.current,
        anchorsRef.current,
      );
      if (activeBucket === targetBucketId) {
        dispatch({ type: "user_action" });
        return;
      }
    }

    if (!step.deferModeNavigation && step.target.mode !== currentMode) {
      onNavigateToModeRef.current(step.target.mode);
      return;
    }

    const prevStepForNav = prevStep;
    const afterBucketPickForNav = afterBucketPick;

    const runStep = async () => {
      const targetBucket = getBucketForMode(step.target.mode);
      const activeBucket = resolveActiveBucket(
        currentModeRef.current,
        authRef.current,
        anchorsRef.current,
      );
      if (
        shouldAutoNavigateAfterBucketPick({
          afterBucketPick: afterBucketPickForNav,
          deferModeNavigation: step.deferModeNavigation === true,
          targetMode: step.target.mode,
          currentMode: currentModeRef.current,
          activeBucket,
          targetBucket,
        })
      ) {
        onNavigateToModeRef.current(step.target.mode);
        const settled = await waitForPrimaryMode(
          () => currentModeRef.current,
          step.target.mode,
          { isCancelled: () => cancelled },
        );
        if (cancelled || !settled) return;
      }

      setHighlightReady(false);
      cancelTravel();

      if (FIREFLY_TOUR_DOCK_ANCHOR_IDS.has(step.target.anchorId)) {
        onOpenDock?.();
        window.dispatchEvent(new Event(ACTIVITY_DOCK_OPEN_EVENT));
      }

      const settleMs = tourStepSettleMs(prevStepForNav?.id);

      await new Promise<void>((resolve) => window.setTimeout(resolve, settleMs));
      if (cancelled) return;

      const waitMs = isFireflyModeNavTarget(step.target.anchorId) ? 5_000 : 2_500;
      const el = await waitForFireflyTarget(step.target.anchorId, { timeoutMs: waitMs });
      if (cancelled) return;

      if (el && step.target.scrollIntoView !== false) {
        scrollFireflyTargetIntoView(el);
        await new Promise<void>((resolve) => window.requestAnimationFrame(() => resolve()));
      }

      const targetPoint = el
        ? (() => {
            const rect = readFireflyTargetRect(el);
            return { x: rect.centerX, y: rect.centerY };
          })()
        : resolveFireflyTourTravelOrigin(step.target.anchorId);

      const fromPoint =
        lastAnchorCenterRef.current ?? resolveFireflyTourTravelOrigin(step.target.anchorId);
      await flyTo(fromPoint, targetPoint);
      if (cancelled) return;

      lastAnchorCenterRef.current = targetPoint;
      setHighlightReady(true);
      bindStepInteractions(el);
    };

    void runStep();

    return () => {
      cancelled = true;
      cancelTravel();
      clearTimer();
      clearClickListener();
    };
  }, [
    anchorsRef,
    authRef,
    cancelTravel,
    clearClickListener,
    clearTimer,
    clickCleanupRef,
    currentMode,
    currentModeRef,
    dispatch,
    flyTo,
    lastAnchorCenterRef,
    onNavigateToModeRef,
    onOpenDock,
    runner.currentIndex,
    runner.currentStep?.step.id,
    runner.status,
    runner.steps,
    setHighlightReady,
    timerRef,
  ]);
}
